/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui imports
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";

// MD components
import MDBox from "components/MDBox";

// Common navbar parts
import CompanyDropdown from "common/Navbars/CompanyDropdown/CompanyDropdown";
import LanguageSelector from "common/Navbars/LanguageSelector/LanguageSelector";

// Styles helpers
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "common/Navbars/DashboardNavbar/styles";

import { useMaterialUIController, setTransparentNavbar } from "context";
import { useCompany } from "context/CompanyContext";
import { useLanguage } from "context/LanguageContext";
import { getRoutes } from "routes";
import api from "services/api";

function DashboardNavbar({ absolute, light, isMini }) {
  const navigate = useNavigate();
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { transparentNavbar, fixedNavbar, darkMode } = controller;
  const theme = useTheme();
  const { t } = useLanguage();
  const { selectedCompany } = useCompany();
  const { pathname } = useLocation();
  // null while loading to allow placeholder rendering (prevents layout shift)
  const [isAdmin, setIsAdmin] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingPdfSplitCount, setPendingPdfSplitCount] = useState(0);
  const menuOpen = Boolean(anchorEl);

  const translatedRoutes = useMemo(() => getRoutes(t), [t]);
  const { navItems, userItem } = useMemo(() => {
    const base = translatedRoutes.filter((r) => r.type === "collapse" && !r.invisible);
    // include user-management unless we definitively know it's false; when null we keep it for placeholder
    const items = base.filter((r) => (r.key === "user-management" ? isAdmin !== false : true));
    const usr = base.find((r) => r.key === "user-management");
    return { navItems: items, userItem: usr };
  }, [translatedRoutes, isAdmin]);

  useEffect(() => {
    // Keep navbar sticky and solid to prevent content overlapping on route changes
    setNavbarType("sticky");
    setTransparentNavbar(dispatch, false);
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/api/admin/me/");
        if (mounted) setIsAdmin(Boolean(data.is_admin));
      } catch (e) {
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadPendingPdfSplitCount = async () => {
      if (!selectedCompany?.id) {
        if (mounted) setPendingPdfSplitCount(0);
        return;
      }
      try {
        const { data } = await api.get("/api/pdf-split-jobs/", {
          params: { company_id: selectedCompany.id, status: "pending" },
        });
        if (mounted) setPendingPdfSplitCount((data.jobs || []).length);
      } catch (e) {
        if (mounted) setPendingPdfSplitCount(0);
      }
    };

    loadPendingPdfSplitCount();
    window.addEventListener("pdfSplitJobsChanged", loadPendingPdfSplitCount);
    const interval = window.setInterval(loadPendingPdfSplitCount, 60000);
    return () => {
      mounted = false;
      window.removeEventListener("pdfSplitJobsChanged", loadPendingPdfSplitCount);
      window.clearInterval(interval);
    };
  }, [selectedCompany?.id, pathname]);

  const renderNavIcon = (item) =>
    item.key === "pdf-split" && pendingPdfSplitCount > 0 ? (
      <Badge color="error" badgeContent={pendingPdfSplitCount} max={99}>
        {item.icon}
      </Badge>
    ) : (
      item.icon
    );

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      return colorValue;
    },
  });

  return (
    <AppBar
      position="sticky"
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, { transparentNavbar: false, absolute: false, light, darkMode }),
        top: 0,
        zIndex: (theme.zIndex?.appBar ?? 1100) + 1,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {isMini ? null : (
          <MDBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              gap: 0.75,
            }}
          >
            <CompanyDropdown />
            <MDBox sx={{ flexGrow: 1 }} />

            {/* Desktop nav */}
            <MDBox sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, alignItems: "center" }}>
              {navItems.map((item) => {
                const active = pathname === item.route || pathname.startsWith(`${item.route}/`);
                return (
                  <Button
                    key={item.key}
                    component={NavLink}
                    to={item.route}
                    size="small"
                    disableRipple
                    sx={{
                      textTransform: "none",
                      fontSize: "0.875rem",
                      minHeight: 34,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      color: active
                        ? theme.palette.primary.main
                        : light || darkMode
                        ? "inherit"
                        : theme.palette.text.primary,
                      backgroundColor: active
                        ? (theme) => theme.palette.action.selected
                        : "transparent",
                      "&.active": { color: (theme) => theme.palette.primary.main },
                    }}
                    startIcon={renderNavIcon(item) || null}
                  >
                    {item.name}
                  </Button>
                );
              })}
              {/* Reserve space for user-management while admin status is loading to avoid layout shift */}
              {isAdmin === null && userItem ? (
                <Button
                  size="small"
                  disableRipple
                  sx={{
                    textTransform: "none",
                    fontSize: "0.875rem",
                    minHeight: 34,
                    px: 1,
                    py: 0.5,
                    visibility: "hidden",
                  }}
                  startIcon={userItem.icon || null}
                >
                  {userItem.name}
                </Button>
              ) : null}
            </MDBox>

            {/* Right controls */}
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center">
              {/* Mobile menu */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={{ display: { xs: "inline-flex", md: "none" }, mr: 0.5 }}
                onClick={handleMenuOpen}
                aria-controls={menuOpen ? "topnav-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  menu
                </Icon>
              </IconButton>
              <Menu
                id="topnav-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                keepMounted
              >
                {navItems.map((item) => {
                  const active = pathname === item.route || pathname.startsWith(`${item.route}/`);
                  return (
                    <MenuItem
                      key={item.key}
                      component={Link}
                      to={item.route}
                      onClick={handleMenuClose}
                      selected={active}
                    >
                      <MDBox display="flex" alignItems="center" gap={1.25}>
                        {renderNavIcon(item)}
                        {item.name}
                      </MDBox>
                    </MenuItem>
                  );
                })}
                {isAdmin === null && userItem ? (
                  <MenuItem disabled>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1.25}
                      sx={{ visibility: "hidden" }}
                    >
                      {userItem.icon}
                      {userItem.name}
                    </MDBox>
                  </MenuItem>
                ) : null}
              </Menu>

              <LanguageSelector />
              <Link to="/authentication/sign-in/basic">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={() => navigate("/settings")}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Defaults and types
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
